<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-th-list me-2 text-primary"></i>
            Prodotti Mappati con Barcode
          </h1>
          <p class="sci-subtitle">Registro dei codici a barre associati ai prodotti di magazzino e cassa</p>
        </div>
      </div>
      <div class="sci-header-right">
        <span class="sci-badge sci-badge-count">
          <i class="fas fa-barcode me-1"></i>
          {{ filteredProdotti.length }} di {{ prodotti.length }} prodotti
        </span>
        <router-link to="/mapprod" class="sci-btn sci-btn-primary">
          <i class="fas fa-camera me-1"></i>
          <span>Scansiona Nuovo</span>
        </router-link>
      </div>
    </header>

    <!-- Main Content Container -->
    <main class="sci-main-content">
      <!-- Search & Filter Bar -->
      <div class="sci-toolbar-card">
        <div class="sci-search-group">
          <i class="fas fa-search search-icon"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Cerca per codice a barre, nome o descrizione..." 
            class="sci-search-input"
          />
          <button 
            v-if="searchQuery" 
            class="clear-search-btn" 
            title="Azzera ricerca"
            @click="searchQuery = ''"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button class="sci-btn sci-btn-secondary" @click="displayProduct" :disabled="isLoading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>

      <!-- Products Table Card -->
      <section class="sci-table-card">
        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th style="width: 280px;">Codice a Barre (Barcode / EAN)</th>
                <th>Nome / Descrizione Prodotto</th>
                <th style="width: 140px; text-align: right;">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="prodotto in filteredProdotti" :key="prodotto.code" class="sci-table-row">
                <!-- Barcode -->
                <td>
                  <div class="barcode-cell">
                    <span class="barcode-badge font-monospace">{{ prodotto.code }}</span>
                    <button 
                      class="btn-copy-code" 
                      :title="copiedCode === prodotto.code ? 'Copiato!' : 'Copia codice'"
                      @click="copyToClipboard(prodotto.code)"
                    >
                      <i class="fas" :class="copiedCode === prodotto.code ? 'fa-check text-success' : 'fa-copy'"></i>
                    </button>
                  </div>
                </td>

                <!-- Description -->
                <td>
                  <div class="product-name-cell">
                    <span class="product-name-text">{{ prodotto.nome || prodotto.descrizio || '—' }}</span>
                  </div>
                </td>

                <!-- Actions -->
                <td style="text-align: right;">
                  <span class="badge-status-mapped">
                    <i class="fas fa-check-circle me-1"></i> Mappato
                  </span>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredProdotti.length === 0">
                <td colspan="3" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-search"></i>
                    </div>
                    <h3 class="empty-title">Nessun prodotto trovato</h3>
                    <p class="empty-desc">
                      {{ searchQuery ? 'Nessun risultato corrisponde alla ricerca "' + searchQuery + '".' : 'Nessun prodotto ancora mappato con codice a barre.' }}
                    </p>
                    <router-link v-if="!searchQuery" to="/mapprod" class="sci-btn sci-btn-primary mt-3">
                      <i class="fas fa-plus me-1"></i> Mappa il primo prodotto
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'TabellaMapP',
  data() {
    return {
      prodotti: [],
      searchQuery: '',
      isLoading: false,
      copiedCode: null,
      store
    }
  },
  computed: {
    filteredProdotti() {
      if (!this.prodotti) return [];
      const q = (this.searchQuery || '').toLowerCase().trim();
      if (!q) return this.prodotti;

      return this.prodotti.filter(p => {
        const code = (p.code || '').toLowerCase();
        const desc = (p.descrizio || '').toLowerCase();
        const nome = (p.nome || '').toLowerCase();
        return code.includes(q) || desc.includes(q) || nome.includes(q);
      });
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" })
    },
    displayProduct() {
      this.isLoading = true;
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;
      this.axios.post(this.store.path + 'displayProduct', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.prodotti = response.data.messaggio;
        } else if (Array.isArray(response.data)) {
          this.prodotti = response.data;
        }
      }).catch(err => {
        console.error('Errore caricamento prodotti:', err);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.copiedCode = text;
        setTimeout(() => {
          if (this.copiedCode === text) {
            this.copiedCode = null;
          }
        }, 2000);
      }).catch(err => {
        console.error('Impossibile copiare il testo:', err);
      });
    }
  },
  beforeMount() {
    this.displayProduct();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* Badges & Buttons */
.sci-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.sci-badge-count {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
  color: #ffffff;
}

.sci-btn-secondary {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
}

/* Main Content */
.sci-main-content {
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Toolbar */
.sci-toolbar-card {
  display: flex;
  gap: 16px;
  align-items: center;
}

.sci-search-group {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 16px;
  color: #94a3b8;
  font-size: 1rem;
}

.sci-search-input {
  width: 100%;
  min-height: 46px;
  padding: 10px 42px 10px 44px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #0f172a;
  box-shadow: 0 2px 6px rgba(0,0,0,0.03);
  transition: all 0.2s ease;
}

.sci-search-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.clear-search-btn {
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  font-size: 0.9rem;
}

.clear-search-btn:hover {
  color: #ef4444;
}

/* Table Card */
.sci-table-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row td {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s ease;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.sci-table-row:last-child td {
  border-bottom: none;
}

/* Barcode Cell */
.barcode-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.barcode-badge {
  background: #f1f5f9;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
}

.btn-copy-code {
  background: none;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy-code:hover {
  background: #ffffff;
  border-color: #94a3b8;
  color: #0f172a;
}

/* Product Name Cell */
.product-name-text {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.badge-status-mapped {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #ecfdf5;
  color: #059669;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
}
</style>
